import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useI18n } from "@/i18n/I18nContext";
import LanguageToggle from "@/components/landing/LanguageToggle";

export default function Navbar() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isHome = location.pathname === "/";

  const anchorLinks = [
    { id: "expertises", label: t.nav.expertises, testid: NAV.linkExpertises },
    { id: "references", label: t.nav.references, testid: NAV.linkReferences },
    { id: "methode", label: t.nav.method, testid: NAV.linkMethode },
    { id: "insights", label: t.nav.insights, testid: "nav-link-insights" },
    { id: "about", label: t.nav.about, testid: NAV.linkAbout },
    { id: "contact", label: t.nav.contact, testid: NAV.linkContact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (e, id) => {
    setOpen(false);
    if (isHome) {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${id}`);
    } else {
      e.preventDefault();
      navigate(`/#${id}`);
    }
  };

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-2 group"
          aria-label="Birrou Advisory & Consulting"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy text-white font-display font-semibold">
            B
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-display font-semibold text-navy text-base">Birrou Advisory</span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {t.nav.brandSub}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {anchorLinks.map((l) => (
            <a
              key={l.id}
              href={isHome ? `#${l.id}` : `/#${l.id}`}
              onClick={(e) => goToAnchor(e, l.id)}
              data-testid={l.testid}
              className="px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:text-navy hover:bg-slate-100 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle compact />
          <a
            href={isHome ? "#contact" : "/#contact"}
            onClick={(e) => goToAnchor(e, "contact")}
            data-testid={NAV.ctaContact}
            className="btn-primary text-sm"
          >
            {t.nav.cta} <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageToggle compact testidPrefix="lang-toggle-mobile" />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={NAV.mobileToggle}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white"
            aria-label={t.nav.menu}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200">
          <div className="container-x py-4 flex flex-col gap-1">
            {anchorLinks.map((l) => (
              <a
                key={l.id}
                href={isHome ? `#${l.id}` : `/#${l.id}`}
                onClick={(e) => goToAnchor(e, l.id)}
                data-testid={`${l.testid}-mobile`}
                className="px-3 py-3 rounded-lg text-base text-slate-800 hover:bg-slate-100"
              >
                {l.label}
              </a>
            ))}
            <a
              href={isHome ? "#contact" : "/#contact"}
              onClick={(e) => goToAnchor(e, "contact")}
              className="btn-primary mt-2"
            >
              {t.nav.cta} <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
